import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Brain, GoogleLogo, SignIn } from "@phosphor-icons/react";

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
            nav("/dashboard");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    const googleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen grid md:grid-cols-2" data-testid="login-page">
            <div className="hidden md:flex bg-slate-950 text-white p-12 flex-col justify-between relative overflow-hidden">
                <div className="absolute inset-0 grid-lines opacity-40" />
                <Link to="/" className="relative flex items-center gap-2 z-10">
                    <div className="w-8 h-8 bg-white flex items-center justify-center">
                        <Brain size={20} color="#0B132B" weight="duotone" />
                    </div>
                    <span className="font-display text-xl">
                        Jobs<span className="text-blue-400">IQ</span>
                    </span>
                </Link>
                <div className="relative z-10">
                    <h1 className="font-display text-5xl leading-tight">
                        Welcome back.
                        <br />
                        The model missed you.
                    </h1>
                    <p className="mt-6 text-slate-400 max-w-md">
                        Your saved roles, video interviews and match history are one sign-in away.
                    </p>
                </div>
                <div className="relative z-10 font-mono-plex text-xs uppercase tracking-widest text-slate-500">
                    Jobs IQ · Recruitment Intelligence · 2026
                </div>
            </div>

            <div className="flex items-center justify-center p-8">
                <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
                    <Link to="/" className="md:hidden font-display text-2xl mb-6 inline-block">
                        Jobs<span className="text-blue-600">IQ</span>
                    </Link>
                    <h2 className="font-display text-3xl">Sign in</h2>
                    <p className="text-sm text-slate-600 mt-2">
                        New here?{" "}
                        <Link to="/register" data-testid="link-to-register" className="text-blue-600 hover:underline">
                            Create an account
                        </Link>
                    </p>

                    <button
                        type="button"
                        onClick={googleLogin}
                        data-testid="google-login-btn"
                        className="mt-8 w-full flex items-center justify-center gap-2 border border-slate-300 py-3 hover:bg-slate-950 hover:text-white hover:border-slate-950 transition-colors"
                    >
                        <GoogleLogo size={18} weight="bold" /> Continue with Google
                    </button>

                    <div className="flex items-center gap-3 my-6 text-xs font-mono-plex uppercase tracking-widest text-slate-500">
                        <div className="flex-1 h-px bg-slate-200" />
                        or with email
                        <div className="flex-1 h-px bg-slate-200" />
                    </div>

                    <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-1">
                        Email
                    </label>
                    <input
                        data-testid="login-email-input"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none bg-white"
                    />

                    <label className="block text-xs font-mono-plex uppercase tracking-widest text-slate-500 mt-4 mb-1">
                        Password
                    </label>
                    <input
                        data-testid="login-password-input"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-3 py-3 border border-slate-300 focus:border-blue-600 focus:outline-none"
                    />

                    <button
                        data-testid="login-submit-btn"
                        disabled={loading}
                        className="mt-6 w-full bg-slate-950 text-white py-3 hover:bg-blue-600 disabled:opacity-60 inline-flex items-center justify-center gap-2"
                    >
                        <SignIn size={18} weight="bold" />
                        {loading ? "Signing in…" : "Sign in"}
                    </button>
                </form>
            </div>
        </div>
    );
}
